import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/ristrutturazioni", label: "Ristrutturazioni" },
  { to: "/edificazioni", label: "Edificazioni" },
  { to: "/operazioni-immobiliari", label: "Operazioni" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/team", label: "Team" },
  { to: "/contatti", label: "Contatti" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const DARK_HERO_PATHS = ["/", "/ristrutturazioni", "/edificazioni", "/operazioni-immobiliari", "/team"];
  const hasDarkHero = DARK_HERO_PATHS.includes(pathname);

  const transparent = hasDarkHero && !scrolled;
  const inkColor = transparent ? "text-risco-bg" : "text-risco-ink";
  const mutedColor = transparent ? "text-stone-300" : "text-stone-600";

  return (
    <header className={`sticky top-0 z-40 transition-all duration-300 ${transparent ? "bg-transparent" : "glass"}`}>
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" className="flex items-center" data-testid="navbar-logo">
          <img
            src={transparent ? "/logo-risco-light.png" : "/logo-risco.png"}
            alt="Ri.Sco. s.r.l."
            style={{ height: '56px', width: 'auto', transition: 'opacity 0.3s ease' }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors relative py-1 ${
                  isActive ? inkColor : `${mutedColor} hover:${transparent ? "text-risco-bg" : "text-risco-ink"}`
                } ${isActive ? "after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-risco-accent" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/simulatore"
            data-testid="navbar-simulatore-cta"
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
              transparent
                ? "bg-risco-bg hover:bg-risco-accent hover:text-risco-bg text-risco-ink"
                : "bg-risco-ink hover:bg-risco-accent text-risco-bg"
            }`}
          >
            Preventivo Smart →
          </Link>
        </div>

        <button
          className={`lg:hidden inline-flex items-center justify-center w-10 h-10 ${inkColor}`}
          onClick={() => setOpen(!open)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-stone-300">
          <div className="container-x py-4 flex flex-col">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `py-3 border-b border-stone-200 text-sm ${isActive ? "text-risco-accent" : "text-stone-700"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/simulatore"
              onClick={() => setOpen(false)}
              data-testid="mobile-navbar-simulatore-cta"
              className="mt-4 px-5 py-3 rounded-full bg-risco-ink text-risco-bg text-sm font-medium text-center"
            >
              Preventivo Smart →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
